import React from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowUpRight } from "lucide-react";
import { Logo } from "./Logo";
import { useI18n } from "../i18n/I18nContext";
import { useSmoothScroll } from "./SmoothScroll";

export default function Footer() {
  const { t } = useI18n();
  const { lang } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { scrollTo } = useSmoothScroll();
  const home = `/${lang || "ar"}`;

  const goToSection = (id) => {
    if (location.pathname === home) scrollTo(`#${id}`);
    else {
      navigate(home);
      setTimeout(() => scrollTo(`#${id}`), 550);
    }
  };

  const nav = [
    { id: "analysis", key: "nav.analysis" },
    { id: "circuit", key: "nav.circuit" },
    { id: "dashboard", key: "nav.dashboard" },
    { id: "transparency", key: "nav.transparency" },
    { id: "contact", key: "nav.contact" },
  ];

  const legal = [
    { to: `${home}/privacy`, key: "privacy.title", tid: "footer-link-privacy" },
    { to: `${home}/cookies`, key: "cookies.title", tid: "footer-link-cookies" },
    { to: `${home}/terms`, key: "terms.title", tid: "footer-link-terms" },
  ];

  return (
    <footer data-testid="site-footer" className="relative bg-ink text-white grain">
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 py-16 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <Logo light />
            <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/60">
              {t("footer.tagline")}
            </p>
            <div className="mt-6 space-y-2.5 text-sm">
              <a
                data-testid="footer-email"
                href={`mailto:${t("footer.email")}`}
                className="flex items-center gap-2.5 text-white/80 hover:text-terracotta transition-colors"
              >
                <Mail size={15} className="text-terracotta" />
                {t("footer.email")}
              </a>
              <a
                data-testid="footer-phone"
                href={`tel:${t("footer.phone").replace(/[^0-9+]/g, "")}`}
                className="flex items-center gap-2.5 text-white/80 hover:text-terracotta transition-colors"
              >
                <Phone size={15} className="text-terracotta" />
                {t("footer.phone")}
              </a>
              <p className="flex items-start gap-2.5 text-white/60">
                <MapPin size={15} className="mt-0.5 shrink-0 text-terracotta" />
                <span>{t("footer.domicilio")}</span>
              </p>
            </div>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-mono text-xs uppercase tracking-widest text-white/40">
              {t("footer.quickTitle")}
            </h4>
            <ul className="mt-5 space-y-3">
              {nav.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    data-testid={`footer-nav-${n.id}`}
                    onClick={() => goToSection(n.id)}
                    className="text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {t(n.key)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-2">
            <h4 className="font-mono text-xs uppercase tracking-widest text-white/40">
              {t("footer.legalTitle")}
            </h4>
            <ul className="mt-5 space-y-3">
              {legal.map((l) => (
                <li key={l.tid}>
                  <Link
                    to={l.to}
                    data-testid={l.tid}
                    className="group inline-flex items-center gap-1 text-sm text-white/70 hover:text-white transition-colors"
                  >
                    {t(l.key)}
                    <ArrowUpRight
                      size={13}
                      className="opacity-0 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 transition-all"
                    />
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="font-mono text-xs uppercase tracking-widest text-white/40">
              {t("footer.companyTitle")}
            </h4>
            <div className="mt-5 space-y-2 text-[13px] leading-relaxed text-white/55">
              <p>{t("footer.razonSocial")}</p>
              <p>{t("footer.cuit")}</p>
              <p>{t("footer.registro")}</p>
              <p>{t("footer.licencia")}</p>
              <p>{t("footer.jurisdiccion")}</p>
              <p>{t("footer.anio")}</p>
              <p>{t("footer.domicilio2")}</p>
            </div>
          </div>
        </div>

        <div className="mt-14 border-t border-white/10 pt-8">
          <p className="text-xs leading-relaxed text-white/40">{t("footer.disclaimer")}</p>
          <p className="mt-4 font-mono text-xs text-white/50">
            © {new Date().getFullYear()} {t("footer.rights")}
          </p>
        </div>
      </div>
    </footer>
  );
}
